import { useEffect, useState, createElement } from "react";

export type MediaNode =
    | {
          mediaContentType: "IMAGE";
          alt?: string;
          image: { url: string; altText?: string };
      }
    | {
          mediaContentType: "VIDEO";
          alt?: string;
          sources: { url: string; mimeType: string; format: string; height: number; width: number }[];
      }
    | {
          mediaContentType: "MODEL_3D";
          alt?: string;
          sources: { url: string; mimeType: string; format: string; filesize: number }[];
      }
    | {
          mediaContentType: "EXTERNAL_VIDEO";
          alt?: string;
          embeddedUrl: string;
      };

/** Returns a stable thumbnail URL for any media node (used in the thumbnail strip). */
export const getMediaThumbnailUrl = (node: MediaNode): string | null => {
    if (node.mediaContentType === "IMAGE") return node.image?.url ?? null;
    if (node.mediaContentType === "VIDEO") return node.sources?.[0]?.url ?? null;
    if (node.mediaContentType === "MODEL_3D") return null; // no preview image available
    if (node.mediaContentType === "EXTERNAL_VIDEO") return null;
    return null;
};

/** Returns the primary URL string used for equality-checking the active item. */
export const getMediaKey = (node: MediaNode): string => {
    if (node.mediaContentType === "IMAGE") return node.image?.url ?? "";
    if (node.mediaContentType === "VIDEO") return node.sources?.[0]?.url ?? "";
    if (node.mediaContentType === "MODEL_3D") return node.sources?.[0]?.url ?? "";
    if (node.mediaContentType === "EXTERNAL_VIDEO") return node.embeddedUrl ?? "";
    return "";
};

/**
 * Converts any YouTube or Vimeo watch URL into the proper iframe-embeddable URL.
 */
export const getEmbedUrl = (url: string): string => {
    if (!url) return url;

    const shortYT = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (shortYT) return `https://www.youtube.com/embed/${shortYT[1]}?autoplay=1`;

    const watchYT = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (watchYT) return `https://www.youtube.com/embed/${watchYT[1]}?autoplay=1`;

    if (url.includes("youtube.com/embed/") || url.includes("player.vimeo.com")) return url;

    const vimeo = url.match(/vimeo\.com\/(\d+)/);
    if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;

    return url;
};

/** Returns a YouTube thumbnail URL if the embeddedUrl is a YouTube video, else null. */
export const getYouTubeThumbnail = (url: string): string | null => {
    const short = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
    if (short) return `https://img.youtube.com/vi/${short[1]}/hqdefault.jpg`;
    const watch = url.match(/[?&]v=([a-zA-Z0-9_-]+)/);
    if (watch) return `https://img.youtube.com/vi/${watch[1]}/hqdefault.jpg`;
    return null;
};

/**
 * Internal component that dynamically loads the Google model-viewer script.
 */
const MODEL_VIEWER_CDN = "https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js";

export const Model3DViewer = ({ src, alt }: { src: string; alt: string }) => {
    const [isReady, setIsReady] = useState<boolean>(() => !!(window as any).customElements?.get("model-viewer"));

    useEffect(() => {
        if ((window as any).customElements?.get("model-viewer")) {
            setIsReady(true);
            return;
        }

        if (!document.querySelector(`script[src*="model-viewer"]`)) {
            const script = document.createElement("script");
            script.type = "module";
            script.src = MODEL_VIEWER_CDN;
            document.head.appendChild(script);
        }

        const interval = setInterval(() => {
            if ((window as any).customElements?.get("model-viewer")) {
                setIsReady(true);
                clearInterval(interval);
            }
        }, 200);

        return () => clearInterval(interval);
    }, []);

    if (!isReady) {
        return (
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    minHeight: 300,
                    gap: 8,
                    color: "#888",
                    fontSize: 14,
                }}
            >
                <span style={{ fontSize: 24 }}>🧊</span>
                Loading 3D viewer…
            </div>
        );
    }

    return createElement("model-viewer", {
        src,
        alt,
        style: { width: "100%", height: "100%", minHeight: 300, display: "block" },
        "camera-controls": "",
        "auto-rotate": "",
        ar: "",
    });
};

/** Renders the main viewer for the active media node. */
export const MediaViewer = ({
    node,
    productTitle,
    mainImageClassName,
}: {
    node: MediaNode | null;
    productTitle: string;
    mainImageClassName?: string;
}) => {
    if (!node) return <div className={mainImageClassName} style={{ background: "#f0f0f0" }} />;

    if (node.mediaContentType === "IMAGE") {
        return <img src={node.image?.url} alt={node.image?.altText || productTitle} className={mainImageClassName} />;
    }

    if (node.mediaContentType === "VIDEO") {
        const mp4 = node.sources?.find((s) => s.mimeType === "video/mp4") ?? node.sources?.[0];
        if (!mp4) return null;
        return (
            <video
                key={mp4.url}
                controls
                loop
                playsInline
                style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }}
            >
                {node.sources.map((s) => (
                    <source key={s.url} src={s.url} type={s.mimeType} />
                ))}
            </video>
        );
    }

    if (node.mediaContentType === "MODEL_3D") {
        const glbSource = node.sources?.find((s) => s.format === "glb") ?? node.sources?.[0];
        if (!glbSource) return null;
        return <Model3DViewer src={glbSource.url} alt={node.alt || productTitle} />;
    }

    if (node.mediaContentType === "EXTERNAL_VIDEO") {
        return (
            <iframe
                key={node.embeddedUrl}
                src={getEmbedUrl(node.embeddedUrl)}
                title={node.alt || productTitle}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: "100%", height: "100%", minHeight: 300, borderRadius: 8, border: "none" }}
            />
        );
    }

    return null;
};
